import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ThemeProvider } from "@/components/ThemeProvider";

// Landing page — lazy-loaded (static HTML shell in index.html handles LCP)
const Index = lazy(() => import("./pages/Index"));

// Lazy-loaded routes
const Auth = lazy(() => import("./pages/Auth"));
const WorkspaceSelector = lazy(() => import("./pages/WorkspaceSelector"));
const Venues = lazy(() => import("./pages/Venues"));
const VenuesFAQ = lazy(() => import("./pages/VenuesFAQ"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const PresenterAccount = lazy(() => import("./pages/PresenterAccount"));
const NewPresentation = lazy(() => import("./pages/NewPresentation"));
const LiveSession = lazy(() => import("./pages/LiveSession"));
const ViewerSession = lazy(() => import("./pages/ViewerSession"));
const SessionEmails = lazy(() => import("./pages/SessionEmails"));
const SessionDetails = lazy(() => import("./pages/SessionDetails"));
const History = lazy(() => import("./pages/History"));
const Emails = lazy(() => import("./pages/Emails"));
const Privacy = lazy(() => import("./pages/Privacy"));
const Terms = lazy(() => import("./pages/Terms"));
const Subprocessors = lazy(() => import("./pages/legal/Subprocessors"));
const DpaRedirect = lazy(() => import("./pages/legal/DpaRedirect"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Status = lazy(() => import("./pages/Status"));
const BillingSuccess = lazy(() => import("./pages/BillingSuccess"));
const BillingCancel = lazy(() => import("./pages/BillingCancel"));
const Billing = lazy(() => import("./pages/Billing"));
const EventViewer = lazy(() => import("./pages/EventViewer"));
const ShortLinkViewer = lazy(() => import("./pages/ShortLinkViewer"));
const EventAssignment = lazy(() => import("./pages/EventAssignment"));
const EventPresenterWorkspace = lazy(() => import("./pages/EventPresenterWorkspace"));
const InviteAccept = lazy(() => import("./pages/InviteAccept"));
const DemoEntry = lazy(() => import("./pages/demo/DemoEntry"));
const DemoVenue = lazy(() => import("./pages/demo/DemoVenue"));
const DemoPresenter = lazy(() => import("./pages/demo/DemoPresenter"));
const DemoViewer = lazy(() => import("./pages/demo/DemoViewer"));
const GetStarted = lazy(() => import("./pages/GetStarted"));
const VenuesGetStarted = lazy(() => import("./pages/VenuesGetStarted"));
const HelpCenter = lazy(() => import("./pages/support/HelpCenter"));
const GuidePage = lazy(() => import("./pages/support/GuidePage"));
const OnboardingWizard = lazy(() => import("./pages/support/OnboardingWizard"));
const Clicker = lazy(() => import("./pages/Clicker"));

// Lazy-loaded route guards & layouts
const ProtectedRoute = lazy(() => import("@/components/ProtectedRoute"));
const AdminRoute = lazy(() => import("@/components/AdminRoute").then(m => ({ default: m.AdminRoute })));
const CenterRoute = lazy(() => import("@/components/CenterRoute").then(m => ({ default: m.CenterRoute })));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetails = lazy(() => import("./pages/admin/AdminUserDetails"));
const AdminPresentations = lazy(() => import("./pages/admin/AdminPresentations"));
const AdminSessions = lazy(() => import("./pages/admin/AdminSessions"));
const AdminSessionDetails = lazy(() => import("./pages/admin/AdminSessionDetails"));
const AdminEmails = lazy(() => import("./pages/admin/AdminEmails"));
const AdminFlags = lazy(() => import("./pages/admin/AdminFlags"));
const AdminVenues = lazy(() => import("./pages/admin/AdminVenues"));
const AdminVenueDetail = lazy(() => import("./pages/admin/AdminVenueDetail"));
const AdminEventDetail = lazy(() => import("./pages/admin/AdminEventDetail"));
const AdminExports = lazy(() => import("./pages/admin/AdminExports"));
const AdminTrials = lazy(() => import("./pages/admin/AdminTrials"));
const AdminLeads = lazy(() => import("./pages/admin/AdminLeads"));
const AdminFreeViews = lazy(() => import("./pages/admin/AdminFreeViews"));
const AdminAudit = lazy(() => import("./pages/admin/AdminAudit"));
const AdminHubSpot = lazy(() => import("./pages/admin/AdminHubSpot"));
const AdminHubSpotSettings = lazy(() => import("./pages/admin/AdminHubSpotSettings"));
const CenterDashboard = lazy(() => import("./pages/center/CenterDashboard"));
const CenterAnalytics = lazy(() => import("./pages/center/CenterAnalytics"));
const CenterEmails = lazy(() => import("./pages/center/CenterEmails"));

const queryClient = new QueryClient();

const App = () => (
  <ErrorBoundary>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <CurrencyProvider>
            <AuthProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <Suspense fallback={<div className="min-h-screen bg-background" />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/get-started" element={<GetStarted />} />
                    <Route path="/for-venues" element={<Navigate to="/venues" replace />} />
                    <Route path="/venues/get-started" element={<VenuesGetStarted />} />
                    <Route path="/venues" element={<Venues />} />
                    <Route path="/venues/faq" element={<VenuesFAQ />} />
                    <Route path="/workspace" element={<WorkspaceSelector />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/auth/invite" element={<InviteAccept />} />
                    <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                    <Route path="/app/account" element={<ProtectedRoute><PresenterAccount /></ProtectedRoute>} />
                    <Route path="/app/presentations/new" element={<ProtectedRoute><NewPresentation /></ProtectedRoute>} />
                    <Route path="/app/sessions/:sessionId" element={<ProtectedRoute><LiveSession /></ProtectedRoute>} />
                    <Route path="/app/sessions/:sessionId/emails" element={<ProtectedRoute><SessionEmails /></ProtectedRoute>} />
                    <Route path="/app/sessions/:sessionId/details" element={<ProtectedRoute><SessionDetails /></ProtectedRoute>} />
                    <Route path="/app/events/:eventId/assign" element={<ProtectedRoute><EventAssignment /></ProtectedRoute>} />
                    <Route path="/app/events/:eventId/presenter" element={<ProtectedRoute><EventPresenterWorkspace /></ProtectedRoute>} />
                    <Route path="/app/history" element={<ProtectedRoute><History /></ProtectedRoute>} />
                    <Route path="/app/emails" element={<ProtectedRoute><Emails /></ProtectedRoute>} />
                    <Route path="/s/:sessionCode" element={<ViewerSession />} />
                    <Route path="/v/:code" element={<ShortLinkViewer />} />
                    <Route path="/e/:eventId/:presentationId" element={<EventViewer />} />
                    <Route path="/app/buy-views" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                    <Route path="/app/upgrade" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                    <Route path="/app/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
                    <Route path="/app/billing/success" element={<ProtectedRoute><BillingSuccess /></ProtectedRoute>} />
                    <Route path="/app/billing/cancel" element={<ProtectedRoute><BillingCancel /></ProtectedRoute>} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/legal/subprocessors" element={<Subprocessors />} />
                    <Route path="/legal/dpa.pdf" element={<DpaRedirect />} />
                    <Route path="/status" element={<Status />} />
                    <Route path="/support" element={<HelpCenter />} />
                    <Route path="/support/guides/:slug" element={<GuidePage />} />
                    <Route path="/support/onboarding/:role" element={<OnboardingWizard />} />
                    <Route path="/clicker/:eventId" element={<Clicker />} />
                    <Route path="/clicker/s/:sessionId" element={<Clicker />} />
                    
                    {/* Demo Routes */}
                    <Route path="/demo" element={<DemoEntry />} />
                    <Route path="/demo/venue" element={<DemoVenue />} />
                    <Route path="/demo/presenter" element={<DemoPresenter />} />
                    <Route path="/demo/viewer" element={<DemoViewer />} />
                    
                    {/* Admin Routes */}
                    <Route path="/admin" element={<AdminRoute><AdminLayout /></AdminRoute>}>
                      <Route index element={<AdminDashboard />} />
                      <Route path="users" element={<AdminUsers />} />
                      <Route path="users/:userId" element={<AdminUserDetails />} />
                      <Route path="presentations" element={<AdminPresentations />} />
                      <Route path="sessions" element={<AdminSessions />} />
                      <Route path="sessions/:sessionId" element={<AdminSessionDetails />} />
                      <Route path="emails" element={<AdminEmails />} />
                      <Route path="flags" element={<AdminFlags />} />
                      <Route path="venues" element={<AdminVenues />} />
                      <Route path="venues/:venueId" element={<AdminVenueDetail />} />
                      <Route path="events/:eventId" element={<AdminEventDetail />} />
                      <Route path="exports" element={<AdminExports />} />
                      <Route path="trials" element={<AdminTrials />} />
                      <Route path="leads" element={<AdminLeads />} />
                      <Route path="free-views" element={<AdminFreeViews />} />
                      <Route path="audit" element={<AdminAudit />} />
                      <Route path="hubspot" element={<AdminHubSpot />} />
                      <Route path="hubspot/settings" element={<AdminHubSpotSettings />} />
                    </Route>
                    
                    {/* Center Routes */}
                    <Route path="/center" element={<CenterRoute><CenterDashboard /></CenterRoute>} />
                    <Route path="/center/analytics" element={<CenterRoute><CenterAnalytics /></CenterRoute>} />
                    <Route path="/center/emails" element={<CenterRoute><CenterEmails /></CenterRoute>} />
                    <Route path="/center/billing/success" element={<CenterRoute><BillingSuccess /></CenterRoute>} />
                    <Route path="/center/billing/cancel" element={<CenterRoute><BillingCancel /></CenterRoute>} />
                    
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </AuthProvider>
          </CurrencyProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </ErrorBoundary>
);

export default App;
